import { z } from 'zod';

// Shipyards' shapes (plan 4C), used by the server and the browser.

export const RecordId = z.string().regex(/^[a-z0-9]{15}$/, 'must be a record id');

export const RecipeCategory = z.enum(['ship', 'item']);
export const BuildType = z.enum(['member', 'corp']);
export const ProjectStatus = z.enum(['gathering', 'on_hold', 'completed']);
export type ProjectStatus = z.infer<typeof ProjectStatus>;

export const RecipeLine = z.object({ material: RecordId, quantity: z.number().int().min(1).max(1e12) });
export type RecipeLine = z.infer<typeof RecipeLine>;

export const Recipe = z.object({ id: z.string(), name: z.string(), category: RecipeCategory, lines: z.array(RecipeLine), active: z.boolean(), version: z.number().int() });
export type Recipe = z.infer<typeof Recipe>;

export const RecipeInput = z.object({ name: z.string().trim().min(1).max(200), category: RecipeCategory.default('ship'), lines: z.array(RecipeLine).min(1).max(40) });
export const RecipeUpdate = RecipeInput.extend({ active: z.boolean().default(true), version: z.number().int().min(1) });

/** Who gets the finished build. */
export const Recipient = z.object({ userId: z.string().nullable(), name: z.string() });
export type Recipient = z.infer<typeof Recipient>;
export const RecipientInput = z.object({ userId: RecordId.nullable().default(null), name: z.string().trim().min(1).max(64) });

export const ProgressLineSchema = z.object({ material: z.string(), name: z.string(), unit: z.enum(['scu', 'units']), hasQuality: z.boolean(), needed: z.number(), have: z.number(), pledged: z.number(), left: z.number() });
export const ProgressSchema = z.object({ percent: z.number().int(), builds: z.object({ target: z.number(), have: z.number(), pledged: z.number() }), lines: z.array(ProgressLineSchema) });

export const Contributor = z.object({ key: z.string(), userId: z.string().nullable(), name: z.string(), value: z.number().int() });
export type Contributor = z.infer<typeof Contributor>;

export const Project = z.object({
  id: z.string(),
  name: z.string(),
  recipe: z.string(),
  recipeName: z.string(),
  category: RecipeCategory,
  type: BuildType,
  status: ProjectStatus,
  creator: z.string(),
  creatorName: z.string(),
  notes: z.string(),
  sortOrder: z.number().int(),
  selfCredit: z.boolean(),
  targetQuantity: z.number().int(),
  recipients: z.array(Recipient),
  minQualities: z.record(z.string(), z.number().int()),
  version: z.number().int(),
  progress: ProgressSchema,
  contributors: z.array(Contributor),
  /** The viewer may edit, hold and finish it (S1). */
  canEdit: z.boolean(),
  /** It has donations, so it can't be deleted (S3). */
  hasDonations: z.boolean(),
});
export type Project = z.infer<typeof Project>;

export const ProjectInput = z.object({
  name: z.string().trim().min(1).max(200),
  recipe: RecordId,
  type: BuildType.default('member'),
  targetQuantity: z.number().int().min(1).max(10000).default(1),
  notes: z.string().trim().max(4000).default(''),
});

export const ProjectUpdate = z.object({
  version: z.number().int().min(1),
  name: z.string().trim().min(1).max(200),
  type: BuildType,
  status: ProjectStatus,
  creator: RecordId,
  notes: z.string().trim().max(4000),
  sortOrder: z.number().int().min(-1e6).max(1e6),
  selfCredit: z.boolean(),
  targetQuantity: z.number().int().min(1).max(10000),
  recipients: z.array(RecipientInput).max(50),
  minQualities: z.record(z.string(), z.number().int().min(0).max(1000)),
});

/** A hand change, in whole SCU or a count. */
export const AdjustInput = z.object({ item: z.string().max(15), amount: z.number().min(-1e9).max(1e9).refine((value) => value !== 0, 'Enter an amount') });

export const DrawInput = z.object({ version: z.number().int().min(1), minimum: z.number().int().min(0), count: z.number().int().min(1).max(20) });

export const MaterialChoice = z.object({ id: z.string(), name: z.string(), unit: z.enum(['scu', 'units']), value: z.number().int(), hasQuality: z.boolean(), active: z.boolean() });
export type MaterialChoice = z.infer<typeof MaterialChoice>;
